import { BackButtonCWUI } from '@next-order/next-order-components';
import { UserLoginRegistrationContext } from 'contexts/UserLoginRegistrationProvider';
import { ORDER_STAGE_TYPES } from 'helpers/enums';
import React, { useContext, useEffect, useRef } from 'react';
import nextLoading from '../../assets/images/next-loading.svg';

export default function MobileNumberComponent({
  orderSetup,
  updateOrderStageBack,
  sidebarAnimationClass,
}) {
  const {
    localState,
    setLocalState,
    updateOrderStageHandler,
    handleInputFieldBlur,
    validateMobileNumber,
  } = useContext(UserLoginRegistrationContext);

  const mobileNumberRef = useRef();
  const emailAddressRef = useRef();

  useEffect(() => {
    // Reset the form fields when component mounts
    setLocalState({
      displayMobileNumber: '',
      emailAddress: '',
      isEmailExists: false,
      fieldErrors: {},
      validatedNumber: '',
      firstName: '',
      lastName: '',
      otpChannel: 'sms',
    });

    mobileNumberRef.current.focus();
  }, [setLocalState]);

  // Focus on email field when it becomes visible
  useEffect(() => {
    if (localState.isEmailExists && emailAddressRef.current) {
      emailAddressRef.current.focus();
    }
  }, [localState.isEmailExists]);

  const isEmailStep =
    orderSetup &&
    orderSetup.enableUserEmail &&
    orderSetup.enableUserEmail === true &&
    localState.isEmailExists;

  const showMobileError =
    !!localState.fieldErrors?.mobileNumber &&
    !!localState.inputFieldBlurStatus?.mobileNumber;

  const showEmailError =
    !!localState.fieldErrors?.emailAddress &&
    !!localState.inputFieldBlurStatus?.emailAddress;

  const isNextDisabled =
    localState.isLoading ||
    !!localState.fieldErrors?.mobileNumber ||
    (localState.isEmailExists && !!localState.fieldErrors?.emailAddress) ||
    !localState.displayMobileNumber ||
    (orderSetup.enableUserEmail &&
      !localState.emailAddress &&
      localState.isEmailExists);

  const handleMobileEnterPress = (e) => {
    if (e.keyCode === 13) {
      if (!orderSetup.enableUserEmail || !localState.isEmailExists) {
        handleMobileNumberEmailProcessNextStep();
      } else {
        emailAddressRef.current.focus();
      }
    }
  };

  const handleEmailEnterPress = (e) => {
    if (e.keyCode === 13) {
      if (
        !localState.fieldErrors.mobileNumber &&
        !localState.fieldErrors.emailAddress
      ) {
        handleMobileNumberEmailProcessNextStep();
      } else {
        mobileNumberRef.current.focus();
      }
    }
  };

  const handleMobileNumberChange = (e) => {
    const mobileNumber = e.target.value;
    validateMobileNumber(mobileNumber);
  };

  const handleEmailAddressChange = (e) => {
    setLocalState({
      emailAddress: e.target.value.trim(),
    });
  };

  const handleMobileNumberEmailProcessNextStep = () => {
    // if (!localState.isNextDisabled) {
    updateOrderStageHandler(ORDER_STAGE_TYPES.VERIFY_FROM);
    // }
  };

  const inputBaseClass =
    'bg-transparent transition-colors duration-200 ease-out focus:border-primary focus:outline-none focus:ring-0 focus:shadow-none placeholder:text-neutral-350';

  return (
    <div className={`${sidebarAnimationClass} detailsConfirm mobileScr active`}>
      <BackButtonCWUI onClick={updateOrderStageBack} />

      {/* Heading */}
      <div className='detailsTitle !pb-2' style={{ marginTop: 0 }}>
        {isEmailStep
          ? `What's your contact details?`
          : `What's your mobile number?`}
      </div>
      <p className='mb-8 pr-6 text-[15px] leading-[22px] text-neutral-525'>
        {isEmailStep
          ? 'We use these to confirm your order and send you updates.'
          : 'We\u2019ll text you a one-time code to verify it\u2019s you.'}
      </p>

      {/* Mobile number */}
      <div className='inputGroup !mb-7'>
        <label htmlFor='mobileNumber'>Mobile Number</label>
        <input
          type='tel'
          inputMode='tel'
          autoComplete='tel'
          enterKeyHint={isEmailStep ? 'next' : 'go'}
          id='mobileNumber'
          name='mobileNumber'
          tabIndex='0'
          ref={mobileNumberRef}
          aria-invalid={showMobileError || undefined}
          aria-describedby={showMobileError ? 'mobileNumber-error' : undefined}
          className={`${inputBaseClass}${showMobileError ? ' error' : ''}`}
          value={localState.displayMobileNumber}
          onKeyDown={handleMobileEnterPress}
          onChange={handleMobileNumberChange}
          onBlur={handleInputFieldBlur}
          autoFocus
        />
        {showMobileError ? (
          <div
            id='mobileNumber-error'
            role='alert'
            className='field-errors !text-[13px] !leading-[18px] !pt-2'
          >
            {localState.fieldErrors['mobileNumber']}
          </div>
        ) : null}
      </div>

      {/* Email (conditional) */}
      {isEmailStep && (
        <div className='inputGroup !mb-7'>
          <label htmlFor='emailAddress'>Email</label>
          <input
            tabIndex='1'
            type='email'
            inputMode='email'
            autoComplete='email'
            autoCapitalize='none'
            spellCheck={false}
            id='emailAddress'
            name='emailAddress'
            enterKeyHint='go'
            ref={emailAddressRef}
            placeholder='you@example.com'
            aria-invalid={showEmailError || undefined}
            aria-describedby={showEmailError ? 'emailAddress-error' : undefined}
            className={`${inputBaseClass}${showEmailError ? ' error' : ''}`}
            value={localState.emailAddress}
            onKeyDown={handleEmailEnterPress}
            onChange={handleEmailAddressChange}
            onBlur={handleInputFieldBlur}
            style={{ textTransform: 'none' }}
          />
          {showEmailError ? (
            <div
              id='emailAddress-error'
              role='alert'
              className='field-errors !text-[13px] !leading-[18px] !pt-2'
            >
              {localState.fieldErrors?.emailAddress}
            </div>
          ) : null}
        </div>
      )}

      <div className='inputGroup'>
        <div id='mobile-submit' />
      </div>

      {/* Primary action */}
      <div
        role='button'
        tabIndex={isNextDisabled ? -1 : 0}
        aria-disabled={isNextDisabled || undefined}
        aria-busy={localState.isLoading || undefined}
        className={`nextDetails${isNextDisabled ? ' disabled' : ''}`}
        onClick={handleMobileNumberEmailProcessNextStep}
        onKeyDown={(e) => {
          if (!isNextDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleMobileNumberEmailProcessNextStep();
          }
        }}
      >
        {localState.isLoading ? <img src={nextLoading} alt='' /> : 'Next'}
        {localState.isLoading ? null : <i className='thickcon-Right-Chevron' />}
      </div>
    </div>
  );
}
